import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/new-article.css';
import api from '../api';
import Icon from './Icon';
import { Spinner } from './ui';

const POLL_INTERVAL = 2500;

const STATUS_LABEL = {
  waiting:    'Waiting for phone…',
  connected:  'Phone connected',
  uploading:  'Upload in progress…',
  done:       'Video received',
  expired:    'Session expired',
  error:      'Error',
};

export default function PhoneRecord({ onVideoReady }) {
  // onVideoReady(videoPath, mode) — 'single' | 'multi'

  const [phase, setPhase] = useState('idle'); // idle | active | done | expired | error
  const [session, setSession] = useState(null);  // {token, url, qr_code, expires_at}
  const [sessionStatus, setSessionStatus] = useState('waiting');
  const [errorMsg, setErrorMsg] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const startSession = useCallback(async () => {
    stopPolling();
    setPhase('active');
    setSessionStatus('waiting');
    setErrorMsg('');
    try {
      const r = await api.post('/phone-sessions');
      setSession(r.data);

      // Countdown timer
      const expiresAt = r.data.expires_at * 1000;
      timerRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
        setTimeLeft(remaining);
        if (remaining === 0) clearInterval(timerRef.current);
      }, 1000);

      // Poll for status
      const token = r.data.token;
      pollRef.current = setInterval(async () => {
        try {
          const res = await api.get(`/phone-sessions/${token}`);
          const s = res.data.status;
          setSessionStatus(s);
          if (s === 'done') {
            stopPolling();
            setPhase('done');
            onVideoReady(res.data.video_path, res.data.mode);
          } else if (s === 'expired') {
            stopPolling();
            setPhase('expired');
          } else if (s === 'error') {
            stopPolling();
            setPhase('error');
            setErrorMsg(res.data.error || 'Unknown error');
          }
        } catch {}
      }, POLL_INTERVAL);

    } catch (err) {
      setPhase('error');
      setErrorMsg(err.response?.data?.error || 'Could not create session');
    }
  }, [onVideoReady]);

  const invalidateAndRegenerate = useCallback(async () => {
    if (session?.token) {
      try { await api.delete(`/phone-sessions/${session.token}`); } catch {}
    }
    setSession(null);
    setTimeLeft(null);
    startSession();
  }, [session, startSession]);

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), []);

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Styles: styles/new-article.css (na-phone*). Also mounted by UpdateArticle,
  // ReportIssue and TestRunModal — keep it self-contained.

  // ── Idle ──────────────────────────────────────────────────────────────────
  if (phase === 'idle') {
    return (
      <div className="na-phone-state">
        <Icon name="video" size={32} />
        <div className="na-subhead">Record from phone</div>
        <p className="na-text">
          Generate a QR code, scan it on your phone, record a walkthrough, and send it here.
        </p>
        <button type="button" className="btn btn-primary" onClick={startSession}>
          Generate QR code
        </button>
      </div>
    );
  }

  // ── Done ──────────────────────────────────────────────────────────────────
  if (phase === 'done') {
    return (
      <div className="na-phone-state is-success" role="status">
        <Icon name="checkCircle" size={32} />
        <div className="na-subhead">Video received from phone</div>
        <p className="na-text">Handing off to the processing pipeline…</p>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
          stopPolling(); setPhase('idle'); setSession(null); setTimeLeft(null);
        }}>
          Record another
        </button>
      </div>
    );
  }

  // ── Expired ───────────────────────────────────────────────────────────────
  if (phase === 'expired') {
    return (
      <div className="na-phone-state" role="status">
        <Icon name="clock" size={32} />
        <div className="na-subhead">Session expired</div>
        <p className="na-text">Sessions expire after 15 minutes for security.</p>
        <button type="button" className="btn btn-primary" onClick={startSession}>
          <Icon name="refresh" /> New QR code
        </button>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className="na-phone-state is-error" role="alert">
        <Icon name="alert" size={32} />
        <div className="na-subhead">Something went wrong</div>
        <p className="na-text">{errorMsg}</p>
        <button type="button" className="btn btn-primary" onClick={startSession}>
          <Icon name="refresh" /> Try again
        </button>
      </div>
    );
  }

  // ── Active — show QR code ─────────────────────────────────────────────────
  const isAnimating = sessionStatus === 'waiting' || sessionStatus === 'uploading';
  const dotTone = sessionStatus === 'connected' || sessionStatus === 'uploading'
    ? 'is-busy'
    : sessionStatus === 'done' ? 'is-done' : sessionStatus === 'error' ? 'is-error' : '';

  return (
    <div className="na-phone">
      {/* Status row */}
      <div className="na-phone-status">
        <span className="na-row" style={{ gap: 8 }} role="status">
          <span className={`na-phone-dot ${dotTone} ${isAnimating ? 'is-pulsing' : ''}`} aria-hidden="true" />
          <span className="na-text">{STATUS_LABEL[sessionStatus] || sessionStatus}</span>
        </span>
        {timeLeft !== null && timeLeft > 0 && (
          <span className={`na-phone-timer ${timeLeft < 60 ? 'is-low' : ''}`}>
            Expires in {fmtTime(timeLeft)}
          </span>
        )}
      </div>

      {/* QR code — three states: loading, available, library-missing */}
      {session?.qr_code ? (
        <div className="na-qr">
          <div className="na-qr-frame">
            <img src={`data:image/png;base64,${session.qr_code}`} alt="QR code that opens the phone recording page" />
          </div>
        </div>
      ) : session && session.qr_available === false ? (
        <div className="na-qr-empty">QR code isn’t available — open the URL below on your phone.</div>
      ) : (
        <div className="na-qr-empty" role="status">
          <Spinner size={16} /> Generating QR code…
        </div>
      )}

      {/* URL fallback */}
      {session?.url && (
        <div>
          <div className="label" id="na-phone-url-label">Or open this URL on your phone</div>
          <div className="na-url-box na-mono" aria-labelledby="na-phone-url-label">{session.url}</div>
        </div>
      )}

      {/* Hint */}
      <p className="field-hint" style={{ marginTop: 0 }}>
        Phone must be on the same Wi-Fi network. Choose Single or Multiple Articles on your phone before recording.
      </p>

      {/* Regenerate */}
      <button type="button" className="btn btn-secondary btn-block" style={{ justifyContent: 'center' }} onClick={invalidateAndRegenerate}>
        <Icon name="refresh" /> Regenerate QR code
      </button>
    </div>
  );
}
